import { ArrowLeft, BarsThree, MagnifyingGlass, XMark } from "@medusajs/icons"
import { useState } from "react"
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom"
import { NAV, SALES_CHANNELS } from "@/lib/nav"
import { useCan, useCurrentMe } from "@/lib/permissions"
import { SETTINGS_NAV, type SettingsItem } from "@/lib/settings-nav"
import { fullName } from "@/lib/format"
import { KeelMark, usePageTitle } from "@/keel/logo"
import { CommandPalette } from "./command-palette"
import { navLabel, useSearchShortcut } from "./app-shell"

/**
 * Shopify's Settings mode: the main nav collapses to an icon rail, a settings
 * list sits beside it, and the page is centred (~700px).
 */
const SHELL_LEFT = "18rem"

export function SettingsShell() {
  const me = useCurrentMe()
  const can = useCan()
  const navigate = useNavigate()
  const location = useLocation()
  const [filter, setFilter] = useState("")
  const [mobile, setMobile] = useState(false)
  const [search, setSearch] = useSearchShortcut()
  const allowed = (i: { perm?: any }) => !i.perm || can(i.perm)
  const items = SETTINGS_NAV.filter(allowed).filter((i) => !filter.trim() || i.label.toLowerCase().includes(filter.trim().toLowerCase()))
  const account = items.filter((i) => i.group === "account")
  const store = items.filter((i) => i.group !== "account")
  const name = fullName(me.user) || me.user.email
  const page = navLabel(location.pathname, SETTINGS_NAV)
  usePageTitle(page ? `${page} · Settings` : "Settings")

  const list = (
    <div className="flex h-full flex-col border-r border-[var(--k-line)] bg-[var(--k-surface)] px-3 pb-2 pt-3 text-[var(--k-ink)]">
      <button onClick={() => navigate("/")} className="k-nav-row mb-2 text-[var(--k-ink)]">
        <ArrowLeft /> Settings
      </button>
      <div className="relative mb-3">
        <MagnifyingGlass className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[var(--k-ink-muted)]" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search settings"
          aria-label="Search settings"
          data-tb-bare
          className="h-8 w-full rounded-[var(--k-r-sm)] bg-[var(--k-sunken)] pl-8 pr-2 text-[13px] text-[var(--k-ink)] shadow-[inset_0_0_0_1px_var(--k-line)] outline-none placeholder:text-[var(--k-ink-muted)] focus:shadow-[inset_0_0_0_2px_var(--k-blue)]"
        />
      </div>
      <nav className="flex flex-1 flex-col gap-y-0.5 overflow-y-auto">
        {account.map((i) => (
          <Item key={i.to} item={i} onNavigate={() => setMobile(false)} />
        ))}
        <div className="mb-1 mt-3 flex items-center gap-x-2 px-2">
          <span className="flex size-7 items-center justify-center rounded-[var(--k-r-sm)] bg-[var(--k-hull)] text-[12px] font-semibold text-white">{me.store.name[0]?.toUpperCase() ?? "K"}</span>
          <div className="min-w-0">
            <div className="truncate text-[13px] font-medium">{me.store.name}</div>
            <div className="text-[11px] text-[var(--k-ink-muted)]">{me.store.platform}</div>
          </div>
        </div>
        {store.map((i) => (
          <Item key={i.to} item={i} onNavigate={() => setMobile(false)} />
        ))}
      </nav>
      <NavLink to="/settings/account" onClick={() => setMobile(false)} className="mt-2 flex items-center gap-x-2 rounded-[var(--k-r-sm)] border-t border-[var(--k-line)] px-2 py-2 hover:bg-[var(--k-sunken)]">
        <span className="flex size-7 items-center justify-center rounded-full bg-[var(--k-tide)] text-[12px] font-semibold text-white">{name[0]?.toUpperCase()}</span>
        <div className="min-w-0">
          <div className="truncate text-[13px] font-medium">{name}</div>
          <div className="truncate text-[11px] text-[var(--k-ink-muted)]">{me.user.email}</div>
        </div>
      </NavLink>
    </div>
  )

  const rail = (
    <div className="flex h-full w-12 flex-col items-center gap-y-1 border-r border-[var(--k-line)] bg-[var(--k-surface)] py-3">
      <NavLink to="/" aria-label="Keel home" className="mb-1">
        <KeelMark size={32} />
      </NavLink>
      <button onClick={() => setSearch(true)} className="mb-2 rounded-[var(--k-r-sm)] p-1.5 text-[var(--k-ink-muted)] hover:bg-[var(--k-sunken)] hover:text-[var(--k-ink)]" aria-label="Search">
        <MagnifyingGlass />
      </button>
      {[...NAV, ...SALES_CHANNELS].filter(allowed).map((n) => {
        const Icon = n.icon
        return (
          <NavLink key={n.to} to={n.to} title={n.label} aria-label={n.label} className="rounded-[var(--k-r-sm)] p-1.5 text-[var(--k-ink-muted)] hover:bg-[var(--k-sunken)] hover:text-[var(--k-ink)]">
            <Icon />
          </NavLink>
        )
      })}
    </div>
  )

  return (
    <div className="flex min-h-screen bg-[var(--k-surface)] lg:h-screen lg:overflow-hidden" style={{ ["--tf-content-left" as any]: SHELL_LEFT }}>
      <aside className="sticky top-0 hidden h-screen shrink-0 lg:flex">
        {rail}
        <div className="w-60">{list}</div>
      </aside>
      {mobile && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setMobile(false)}>
          <div className="absolute inset-0 bg-[rgba(11,26,36,0.4)]" />
          <aside className="absolute inset-y-0 left-0 w-64 shadow-[var(--tb-shadow-pop)]" onClick={(e) => e.stopPropagation()}>
            {list}
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col lg:h-screen">
        <header className="sticky top-0 z-20 flex items-center gap-x-2 border-b border-[var(--k-line)] bg-[var(--k-surface)] px-3 py-2 text-[var(--k-ink)] lg:hidden">
          <button onClick={() => setMobile(true)} className="rounded-[var(--k-r-sm)] p-1.5 text-[var(--k-ink-muted)]" aria-label="Settings menu">
            <BarsThree />
          </button>
          <KeelMark size={28} />
          <span className="flex-1 text-[13px] font-medium">Settings</span>
          <button onClick={() => navigate("/")} className="rounded-[var(--k-r-sm)] p-1.5 text-[var(--k-ink-muted)]" aria-label="Close settings">
            <XMark />
          </button>
        </header>
        <div className="relative min-h-0 flex-1 overflow-y-auto bg-[var(--k-surface)]">
          <main key={location.pathname} className="mx-auto w-full max-w-[760px] px-4 py-8 lg:px-8">
            <Outlet />
          </main>
        </div>
      </div>
      <CommandPalette open={search} onOpenChange={setSearch} onGo={(to) => navigate(to)} />
    </div>
  )
}

function Item({ item, onNavigate }: { item: SettingsItem; onNavigate: () => void }) {
  const Icon = item.icon
  return (
    <NavLink to={item.to} onClick={onNavigate} className={({ isActive }) => "k-nav-row" + (isActive ? " is-active" : item.soon ? " opacity-70" : "")}>
      <Icon />
      <span className="truncate">{item.label}</span>
    </NavLink>
  )
}
